import axios from 'axios'
import { YouTubeVideo } from '../types'

// Runtime environment config interface
interface RuntimeEnv {
  VITE_YOUTUBE_API_KEY?: string
  VITE_API_URL?: string
}

// Extend Window interface to include runtime config
declare global {
  interface Window {
    _env_?: RuntimeEnv
  }
}

// Support both build-time and runtime environment variables
const getYouTubeApiKey = () => {
  // Try runtime config first (for production)
  if (typeof window !== 'undefined' && window._env_?.VITE_YOUTUBE_API_KEY) {
    return window._env_.VITE_YOUTUBE_API_KEY
  }
  // Fall back to build-time env (for development)
  return import.meta.env.VITE_YOUTUBE_API_KEY
}

const YOUTUBE_API_KEY = getYouTubeApiKey()
const YOUTUBE_API_BASE = 'https://www.googleapis.com/youtube/v3'

// Channel handles (usernames) - must match exact @handles from YouTube
const CHANNEL_HANDLES = {
  martenMindbender: '@Marten.Mindbender',
  whenDiscoGoesWrong: '@whendiscogoeswrong4111'
}

// Cache for resolved channel IDs
const channelIdCache: { [key: string]: string } = {}

async function getChannelIdFromHandle(handle: string): Promise<string | null> {
  if (channelIdCache[handle]) {
    return channelIdCache[handle]
  }

  try {
    // Use forHandle parameter (YouTube API v3)
    const response = await axios.get(`${YOUTUBE_API_BASE}/channels`, {
      params: {
        key: YOUTUBE_API_KEY,
        part: 'id',
        forHandle: handle.replace('@', '')
      }
    })

    if (response.data.items && response.data.items.length > 0) {
      const channelId = response.data.items[0].id
      channelIdCache[handle] = channelId
      console.log(`Resolved ${handle} to channel ID: ${channelId}`)
      return channelId
    }

    console.error(`Could not resolve handle ${handle} to a channel ID`)
    return null
  } catch (error) {
    console.error(`Error resolving channel handle ${handle}:`, error)
    return null
  }
}

interface YouTubeAPIResponse {
  items: {
    id: {
      videoId: string
    }
    snippet: {
      title: string
      description: string
      thumbnails: {
        medium: {
          url: string
        }
      }
      publishedAt: string
      channelTitle: string
      channelId: string
    }
  }[]
}

async function fetchChannelVideos(channelId: string, maxResults: number = 3): Promise<YouTubeVideo[]> {
  try {
    const response = await axios.get<YouTubeAPIResponse>(`${YOUTUBE_API_BASE}/search`, {
      params: {
        key: YOUTUBE_API_KEY,
        channelId: channelId,
        part: 'snippet',
        order: 'date',
        maxResults: maxResults,
        type: 'video'
      }
    })

    return response.data.items.map(item => ({
      id: item.id.videoId,
      title: item.snippet.title,
      description: item.snippet.description,
      thumbnailUrl: item.snippet.thumbnails.medium.url,
      publishedAt: item.snippet.publishedAt,
      channelTitle: item.snippet.channelTitle,
      channelId: item.snippet.channelId
    }))
  } catch (error) {
    console.error(`Error fetching videos for channel ${channelId}:`, error)
    return []
  }
}

export async function fetchAlternatingVideos(videosPerChannel: number = 3): Promise<YouTubeVideo[]> {
  // Resolve channel handles to IDs
  const [martenChannelId, wdgwChannelId] = await Promise.all([
    getChannelIdFromHandle(CHANNEL_HANDLES.martenMindbender),
    getChannelIdFromHandle(CHANNEL_HANDLES.whenDiscoGoesWrong)
  ])

  if (!martenChannelId || !wdgwChannelId) {
    console.error('Failed to resolve one or more channel handles')
    return []
  }

  const [martenVideos, wdgwVideos] = await Promise.all([
    fetchChannelVideos(martenChannelId, videosPerChannel),
    fetchChannelVideos(wdgwChannelId, videosPerChannel)
  ])

  // Alternate videos from both channels
  const alternatingVideos: YouTubeVideo[] = []
  const maxLength = Math.max(martenVideos.length, wdgwVideos.length)

  for (let i = 0; i < maxLength; i++) {
    if (i < martenVideos.length) {
      alternatingVideos.push(martenVideos[i])
    }
    if (i < wdgwVideos.length) {
      alternatingVideos.push(wdgwVideos[i])
    }
  }

  return alternatingVideos
}
